import { useState, useRef, useEffect } from "react";
import { Brain, Send, Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DashboardLayout } from "@/pages/DashboardPage";
import { useAuth, API } from "@/App";
import axios from "axios";

export default function ChatPage() {
  const { token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMsg }]);
    setLoading(true);
    try {
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const resp = await axios.post(`${API}/chat/message`, { message: userMsg, session_id: sessionId }, { headers, withCredentials: true });
      setMessages((prev) => [...prev, { role: "assistant", content: resp.data.response }]);
      if (resp.data.session_id) setSessionId(resp.data.session_id);
    } catch {
      setMessages((prev) => [...prev, { role: "assistant", content: "I apologize, I encountered an error. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setSessionId(null);
  };

  return (
    <DashboardLayout title="ZyberGuard AI">
      <div className="flex flex-col h-[calc(100vh-8rem)]" data-testid="chat-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-purple-600/10 flex items-center justify-center">
              <Brain className="w-5 h-5 text-purple-400" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-base font-semibold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>ZyberGuard AI</h2>
              <p className="text-xs text-zinc-500">Cybersecurity expert powered by GPT-5.2</p>
            </div>
          </div>
          <Button onClick={startNewChat} variant="outline" className="border-zinc-800 text-zinc-400 hover:bg-zinc-900 hover:text-zinc-50 rounded-sm bg-transparent text-sm" data-testid="new-chat-btn">
            <Plus className="w-4 h-4 mr-1" /> New Chat
          </Button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto trace-border rounded-sm bg-zinc-900 p-4 space-y-4">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Brain className="w-12 h-12 text-zinc-700 mb-4" strokeWidth={1} />
              <h3 className="text-base font-semibold text-zinc-400" style={{ fontFamily: 'var(--font-heading)' }}>ZyberGuard AI Assistant</h3>
              <p className="text-sm text-zinc-600 mt-2 max-w-md">Your 24/7 cybersecurity expert. Ask about threat analysis, incident response, compliance, network security, or any security concern.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-6 max-w-lg">
                {[
                  { q: "How to protect against ransomware?", icon: "shield" },
                  { q: "Explain zero trust architecture", icon: "network" },
                  { q: "Best practices for API security", icon: "code" },
                  { q: "GDPR compliance checklist", icon: "list" },
                  { q: "How to detect phishing emails?", icon: "mail" },
                  { q: "Set up a home network firewall", icon: "router" },
                ].map((item) => (
                  <button
                    key={item.q}
                    onClick={() => { setInput(item.q); }}
                    className="text-xs px-3 py-2 bg-zinc-800 text-zinc-400 rounded-sm border border-zinc-700 hover:bg-zinc-700 hover:text-zinc-200 transition-colors text-left"
                    data-testid={`suggested-q-${item.q.slice(0, 10).replace(/\s/g, '-').toLowerCase()}`}
                  >
                    {item.q}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${i}`}>
              <div className={`max-w-[80%] rounded-sm p-3 text-sm leading-relaxed ${
                msg.role === "user"
                  ? "bg-blue-600 text-white"
                  : "bg-zinc-800 text-zinc-200 border border-zinc-700"
              }`}>
                <div className="whitespace-pre-wrap">{msg.content}</div>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-zinc-800 border border-zinc-700 rounded-sm p-3 flex items-center gap-2">
                <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                <span className="text-sm text-zinc-400">Analyzing...</span>
              </div>
            </div>
          )}
          <div ref={scrollRef} />
        </div>

        {/* Input */}
        <form onSubmit={sendMessage} className="mt-4 flex gap-3">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about cybersecurity..."
            className="bg-zinc-900 border-zinc-800 focus:ring-1 focus:ring-blue-500 rounded-sm h-11 text-zinc-50 placeholder:text-zinc-600"
            disabled={loading}
            data-testid="chat-input"
          />
          <Button type="submit" disabled={loading || !input.trim()} className="bg-blue-600 hover:bg-blue-500 text-white h-11 px-5 rounded-sm" data-testid="chat-send-btn">
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>
    </DashboardLayout>
  );
}
